import { ChevronDown } from "lucide-react";
import moment from "moment";
import { usePathname as useFullPathName } from "next/navigation";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Link, usePathname } from "@/lib/i18n";
import * as m from "@/paraglide/messages";
import { useAuth } from "@/services/context/auth/AuthContext";
import { saveUserLanguage } from "@/services/controllers/user";
import { useAppSelector } from "@/services/hooks/redux.hooks";
import useScreenSize from "@/services/hooks/screen.hooks";

export default function LanguageSwitcher() {
  const { width } = useScreenSize();
  const pathname = usePathname();
  const fullPathname = useFullPathName();
  const { user } = useAuth();

  const { language, loading } = useAppSelector((state) => state.user);

  const [currentLanguage, setCurrentLanguage] = useState<string>("English");
  const [loadingLang, setLoadingLang] = useState<string>("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (fullPathname.includes("/fr") || language === "fr") {
        setCurrentLanguage("French");
        moment.locale("fr");
      } else {
        setCurrentLanguage("English");
        moment.locale("en");
      }
    }
  }, [fullPathname, loading, language]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="items-center text-xs md:flex"
        >
          <span>
            {width <= 450
              ? currentLanguage === "French"
                ? "FR"
                : "EN"
              : currentLanguage ?? "English"}
          </span>
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>{m.select_language()}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem
            className={
              currentLanguage === "English"
                ? "bg-accent"
                : "cursor-pointer w-full"
            }
            disabled={currentLanguage === "English"}
            onClick={async (e) => {
              if (user?.email) {
                e.preventDefault();
                setLoadingLang("en");
                await saveUserLanguage("en", {});
              }
              const link = document.getElementById("en-btn");

              window.location.href = (link as HTMLAnchorElement).href;
            }}
          >
            <Link
              href={pathname}
              locale="en"
              hrefLang="en"
              className="w-full"
              id="en-btn"
            >
              <div className="flex">
                {loadingLang == "en" ? m.changing() : m.english()}
              </div>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem
            className={
              currentLanguage === "French"
                ? "bg-accent"
                : "cursor-pointer w-full"
            }
            disabled={currentLanguage === "French"}
            onClick={async (e) => {
              if (user?.email) {
                e.preventDefault();
                setLoadingLang("fr");
                await saveUserLanguage("fr", {});
              }
              const link = document.getElementById("fr-btn");

              window.location.href = (link as HTMLAnchorElement).href;
            }}
          >
            <Link
              href={pathname}
              locale="fr"
              hrefLang="fr"
              className="w-full"
              id="fr-btn"
            >
              {loadingLang == "fr" ? m.changing() : m.french()}
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
